import { cn } from "@shared/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { BaseSlot } from "./slot";

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        brand: "bg-brand text-brand-on hover:bg-brand-hover",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border border-foreground/85 bg-background hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline underline-offset-4 hover:text-primary/80",
      },
      size: {
        // With a leading icon, keep left padding tight and add extra right padding
        // so the label doesn't look cramped against the pill edge.
        default: "h-9 px-4 py-2 has-[>svg]:pl-3 has-[>svg]:pr-5",
        sm: "h-8 gap-1.5 px-3.5 has-[>svg]:pl-2.5 has-[>svg]:pr-4",
        lg: "h-10 px-6 has-[>svg]:pl-4 has-[>svg]:pr-7",
        icon: "size-9",
      },
      width: {
        default: "",
        full: "w-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  width,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const classes = cn(buttonVariants({ variant, size, width, className }));

  if (asChild) {
    return <BaseSlot data-slot="button" className={classes} {...(props as React.ComponentProps<typeof BaseSlot>)} />;
  }

  return <button data-slot="button" className={classes} {...props} />;
}

export { Button, buttonVariants };
